// ==========================================
// DATE CONSTANTS
// ==========================================

/** Milliseconds in one calendar day */
export const MS_PER_DAY = 86_400_000;

/**
 * Number of calendar days between two UTC-midnight Dates.
 * Positive if b is after a, negative if b is before a.
 */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
}

// ==========================================
// DATE PARSING & "TODAY" UTILITIES
// ==========================================

/**
 * Parse a YYYY-MM-DD calendar date string into a Date at UTC midnight.
 * This is the canonical way to convert server date strings into Date objects.
 * All downstream code should use UTC methods (getUTCFullYear, getUTCDate, etc.)
 */
export function parseCalendarDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Get today's date as a UTC-midnight Date, based on the user's local clock.
 * Reads local date components and wraps them in Date.UTC() so the result
 * can be compared with parseCalendarDate() results using UTC methods.
 */
export function getLocalToday(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

/**
 * Format a UTC-midnight Date as a 'YYYY-MM-DD' string using UTC components.
 */
export function toDateStr(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Get today's date as a 'YYYY-MM-DD' string based on the user's local clock.
 * Convenience wrapper around getLocalToday() + toDateStr().
 */
export function getTodayStr(): string {
  return toDateStr(getLocalToday());
}

// ==========================================
// DATE FORMATTING
// ==========================================

/**
 * Format a UTC-midnight Date using toLocaleDateString with timeZone: 'UTC'.
 * This ensures the displayed date matches the UTC calendar date,
 * preventing off-by-one errors in negative-offset timezones.
 */
export function formatCalendarDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  return date.toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });
}

// ==========================================
// MONTH UTILITIES
// ==========================================

// Helper to get month display name
export function getMonthDisplayName(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1));
  return formatCalendarDate(date, { month: 'long', year: 'numeric' });
}

// Helper to get current month key
export function getCurrentMonthKey(): string {
  const today = getLocalToday();
  return `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Check if a month is in the future
export function isFutureMonth(monthKey: string): boolean {
  const current = getCurrentMonthKey();
  return monthKey > current;
}

// Check if a month is current
export function isCurrentMonth(monthKey: string): boolean {
  return monthKey === getCurrentMonthKey();
}

// Get previous month key
export function getPreviousMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  return `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
}

// Get next month key
export function getNextMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
}

// Get year from month key
export function getYearFromMonthKey(monthKey: string): number {
  return parseInt(monthKey.split('-')[0]);
}

// Navigate months forward/backward by count
export function navigateMonths(monthKey: string, count: number): string {
  const [year, month] = monthKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1 + count, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Navigate to previous month timeframe based on range
export function getPreviousMonthTimeframe(monthKey: string, range: string): string {
  switch (range) {
    case '3-months':
      return navigateMonths(monthKey, -3);
    case '6-months':
      return navigateMonths(monthKey, -6);
    case 'quarter': {
      const [year, month] = monthKey.split('-').map(Number);
      const quarterStart = Math.floor((month - 1) / 3) * 3 + 1;
      // Go to first month of previous quarter
      const prevQuarterFirst =
        quarterStart === 1
          ? `${year - 1}-10`
          : `${year}-${String(quarterStart - 3).padStart(2, '0')}`;
      return prevQuarterFirst;
    }
    case 'year':
      return navigateMonths(monthKey, -12);
    default:
      return getPreviousMonthKey(monthKey);
  }
}

// Navigate to next month timeframe based on range
export function getNextMonthTimeframe(monthKey: string, range: string): string {
  switch (range) {
    case '3-months':
      return navigateMonths(monthKey, 3);
    case '6-months':
      return navigateMonths(monthKey, 6);
    case 'quarter': {
      const [year, month] = monthKey.split('-').map(Number);
      const quarterStart = Math.floor((month - 1) / 3) * 3 + 1;
      // Go to first month of next quarter
      const nextQuarterFirst =
        quarterStart === 10
          ? `${year + 1}-01`
          : `${year}-${String(quarterStart + 3).padStart(2, '0')}`;
      return nextQuarterFirst;
    }
    case 'year':
      return navigateMonths(monthKey, 12);
    default:
      return getNextMonthKey(monthKey);
  }
}

// Get short month name (e.g., "Jan")
export function getShortMonthName(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1));
  return formatCalendarDate(date, { month: 'short' });
}

// Get short month name with full year (e.g., "Jan 2026")
export function getShortMonthWithYear(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1));
  const monthStr = formatCalendarDate(date, { month: 'short' });
  return `${monthStr} ${year}`;
}

// Get month number from month key (1-12)
export function getMonthNumber(monthKey: string): number {
  return parseInt(monthKey.split('-')[1]);
}

// ==========================================
// WEEK UTILITIES (ISO 8601)
// ==========================================

/**
 * Get ISO week number for a date
 * ISO 8601: Week 1 is the week containing the first Thursday of the year
 */
export function getISOWeekNumber(date: Date): number {
  // Use UTC methods to avoid timezone issues
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  // Set to nearest Thursday: current date + 4 - current day number (Mon=1, Sun=7)
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate week number
  const weekNum = Math.ceil(((d.getTime() - yearStart.getTime()) / MS_PER_DAY + 1) / 7);
  return weekNum;
}

/**
 * Get ISO week year (may differ from calendar year at year boundaries)
 */
export function getISOWeekYear(date: Date): number {
  // Use UTC methods to avoid timezone issues
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
}

/**
 * Get week key for a date using Sunday-starting weeks
 * We use ISO week numbers but shift so Sunday starts the week
 * (ISO considers Sunday as the last day of a week)
 */
export function getWeekKey(date: Date): string {
  // For Sunday-starting weeks: if it's Sunday, use the next day (Monday)
  // to get the ISO week number, since that Monday is in the same
  // "Sunday-starting week" as the Sunday
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

  // If it's Sunday (day 0), add 1 day to get Monday for ISO week calculation
  if (d.getUTCDay() === 0) {
    d.setUTCDate(d.getUTCDate() + 1);
  }

  const year = getISOWeekYear(d);
  const week = getISOWeekNumber(d);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/**
 * Get current week key (Sunday-starting week)
 * Uses local date components so the user's timezone determines "today"
 */
export function getCurrentWeekKey(): string {
  return getWeekKey(getLocalToday());
}

/**
 * Parse week key to get the Sunday (start) of that week
 * Note: We use ISO week numbers but with Sunday-starting weeks
 */
export function getWeekStart(weekKey: string): Date {
  const match = weekKey.match(/(\d{4})-W(\d{2})/);
  if (!match) throw new Error(`Invalid week key: ${weekKey}`);

  const year = parseInt(match[1]);
  const week = parseInt(match[2]);

  // Find Jan 4 of the year (always in week 1 per ISO)
  const jan4 = new Date(Date.UTC(year, 0, 4));
  // Find the Monday of week 1 (ISO reference point)
  const dayOfWeek = jan4.getUTCDay() || 7; // Mon=1, Sun=7
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (dayOfWeek - 1));

  // Get Monday of target week
  const targetMonday = new Date(week1Monday);
  targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);

  // Return Sunday (1 day before Monday) as week start
  const targetSunday = new Date(targetMonday);
  targetSunday.setUTCDate(targetMonday.getUTCDate() - 1);

  return targetSunday;
}

/**
 * Get the Saturday (end) of the week
 */
export function getWeekEnd(weekKey: string): Date {
  const sunday = getWeekStart(weekKey);
  const saturday = new Date(sunday);
  saturday.setUTCDate(sunday.getUTCDate() + 6);
  return saturday;
}

/**
 * Get display range for a week (e.g., "Jan 20-26" or "Jan 27-Feb 2")
 */
export function getWeekDisplayRange(weekKey: string): string {
  const start = getWeekStart(weekKey);
  const end = getWeekEnd(weekKey);

  const startMonth = formatCalendarDate(start, { month: 'short' });
  const startDay = start.getUTCDate();
  const endMonth = formatCalendarDate(end, { month: 'short' });
  const endDay = end.getUTCDate();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}`;
  } else {
    return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
  }
}

/**
 * Get display range with year (e.g., "Jan 20-26, 2026")
 */
export function getWeekDisplayRangeWithYear(weekKey: string): string {
  const start = getWeekStart(weekKey);
  const year = start.getUTCFullYear();
  return `${getWeekDisplayRange(weekKey)}, ${year}`;
}

/**
 * Get previous week key
 */
export function getPreviousWeekKey(weekKey: string): string {
  const match = weekKey.match(/(\d{4})-W(\d{2})/);
  if (!match) throw new Error(`Invalid week key: ${weekKey}`);

  let year = parseInt(match[1]);
  let week = parseInt(match[2]);

  week--;
  if (week < 1) {
    year--;
    // Check if previous year has 53 weeks
    const dec31 = new Date(Date.UTC(year, 11, 31));
    week = getISOWeekNumber(dec31) === 53 ? 53 : 52;
  }

  return `${year}-W${String(week).padStart(2, '0')}`;
}

/**
 * Get next week key
 */
export function getNextWeekKey(weekKey: string): string {
  const match = weekKey.match(/(\d{4})-W(\d{2})/);
  if (!match) throw new Error(`Invalid week key: ${weekKey}`);

  let year = parseInt(match[1]);
  let week = parseInt(match[2]);

  // Check how many weeks in current year
  const dec31 = new Date(Date.UTC(year, 11, 31));
  const maxWeek = getISOWeekNumber(dec31) === 53 ? 53 : 52;

  week++;
  if (week > maxWeek) {
    year++;
    week = 1;
  }

  return `${year}-W${String(week).padStart(2, '0')}`;
}

/**
 * Check if a week is the current week
 */
export function isCurrentWeek(weekKey: string): boolean {
  return weekKey === getCurrentWeekKey();
}

/**
 * Check if a week is in the future
 */
export function isFutureWeek(weekKey: string): boolean {
  return weekKey > getCurrentWeekKey();
}

/**
 * Check if a week is in the past
 */
export function isPastWeek(weekKey: string): boolean {
  return weekKey < getCurrentWeekKey();
}

/**
 * Get the month key for a week (based on week start date)
 */
export function weekKeyToMonthKey(weekKey: string): string {
  const monday = getWeekStart(weekKey);
  const year = monday.getUTCFullYear();
  const month = monday.getUTCMonth() + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Get week number from week key (1-53)
 */
export function getWeekNumber(weekKey: string): number {
  const match = weekKey.match(/(\d{4})-W(\d{2})/);
  if (!match) throw new Error(`Invalid week key: ${weekKey}`);
  return parseInt(match[2]);
}

/**
 * Get year from week key
 */
export function getYearFromWeekKey(weekKey: string): number {
  const match = weekKey.match(/(\d{4})-W(\d{2})/);
  if (!match) throw new Error(`Invalid week key: ${weekKey}`);
  return parseInt(match[1]);
}

// ==========================================
// TIMEFRAME NAVIGATION
// ==========================================

/**
 * Navigate weeks by a given count (positive = forward, negative = backward)
 */
export function navigateWeeks(weekKey: string, count: number): string {
  let result = weekKey;
  const absCount = Math.abs(count);
  const navigateFn = count > 0 ? getNextWeekKey : getPreviousWeekKey;

  for (let i = 0; i < absCount; i++) {
    result = navigateFn(result);
  }

  return result;
}

/**
 * Get the first week that starts IN a month (Sunday falls in that month)
 */
export function getFirstWeekOfMonth(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  // First day of month
  const firstDay = new Date(Date.UTC(year, month - 1, 1));
  const dayOfWeek = firstDay.getUTCDay(); // 0 = Sunday

  let firstSunday: Date;
  if (dayOfWeek === 0) {
    // First day is Sunday - this week starts in this month
    firstSunday = firstDay;
  } else {
    // Find the NEXT Sunday (first Sunday that falls IN this month)
    firstSunday = new Date(firstDay);
    firstSunday.setUTCDate(firstDay.getUTCDate() + (7 - dayOfWeek));
  }

  return getWeekKey(firstSunday);
}

/**
 * Navigate to previous timeframe based on period
 */
export function getPreviousTimeframe(weekKey: string, period: string): string {
  switch (period) {
    case 'month': {
      // Go to first week of previous month
      const monthKey = weekKeyToMonthKey(weekKey);
      const prevMonthKey = getPreviousMonthKey(monthKey);
      return getFirstWeekOfMonth(prevMonthKey);
    }
    case '5':
      return navigateWeeks(weekKey, -5);
    case '7':
      return navigateWeeks(weekKey, -7);
    case 'quarter': {
      // Go to first week of previous quarter
      const year = getYearFromWeekKey(weekKey);
      const week = getWeekNumber(weekKey);
      let targetWeek: number;
      let targetYear = year;

      if (week >= 40) targetWeek = 27;
      else if (week >= 27) targetWeek = 14;
      else if (week >= 14) targetWeek = 1;
      else {
        // Go to Q4 of previous year (W40)
        targetYear = year - 1;
        targetWeek = 40;
      }

      return `${targetYear}-W${String(targetWeek).padStart(2, '0')}`;
    }
    default:
      return getPreviousWeekKey(weekKey);
  }
}

/**
 * Navigate to next timeframe based on period
 */
export function getNextTimeframe(weekKey: string, period: string): string {
  switch (period) {
    case 'month': {
      // Go to first week of next month
      const monthKey = weekKeyToMonthKey(weekKey);
      const nextMonthKey = getNextMonthKey(monthKey);
      return getFirstWeekOfMonth(nextMonthKey);
    }
    case '5':
      return navigateWeeks(weekKey, 5);
    case '7':
      return navigateWeeks(weekKey, 7);
    case 'quarter': {
      // Go to first week of next quarter
      const year = getYearFromWeekKey(weekKey);
      const week = getWeekNumber(weekKey);
      let targetWeek: number;
      let targetYear = year;

      if (week < 14) targetWeek = 14;
      else if (week < 27) targetWeek = 27;
      else if (week < 40) targetWeek = 40;
      else {
        // Go to Q1 of next year (W01)
        targetYear = year + 1;
        targetWeek = 1;
      }

      return `${targetYear}-W${String(targetWeek).padStart(2, '0')}`;
    }
    default:
      return getNextWeekKey(weekKey);
  }
}

// ==========================================
// CATEGORY & EVENT STYLING
// ==========================================

// Category color mapping (legacy — used by views not yet migrated)
export const categoryColors: Record<string, string> = {
  tech: 'bg-blue-100 border-blue-200',
  health: 'bg-green-100 border-green-200',
  personal: 'bg-pink-100 border-pink-200',
  finance: 'bg-yellow-100 border-yellow-200',
  travel: 'bg-indigo-100 border-indigo-200',
  other: 'bg-gray-100 border-gray-200',
};

export const categoryIcons: Record<string, string> = {
  tech: '💻',
  health: '🏃',
  personal: '👤',
  finance: '💰',
  travel: '✈️',
  other: '📋',
};

// Status-driven chip styling (new unified system)
// Left border accent colored by status, neutral background
export const statusAccentColors: Record<string, string> = {
  planned: 'status-planned',
  active: 'status-active',
  almost: 'status-almost',
  done: 'status-done',
};

// ==========================================
// EVENT FILTERING UTILITIES
// ==========================================
// These handle multi-day events properly by checking if the event's
// [date, endDate] range overlaps the target period.

import type { CalendarEvent } from './types';

/** Get effective end date for an event (falls back to date if no endDate) */
export function eventEndDate(event: CalendarEvent): string {
  return event.endDate || event.date;
}

/** Check if event overlaps a single day */
export function eventOverlapsDay(event: CalendarEvent, dayStr: string): boolean {
  return event.date <= dayStr && eventEndDate(event) >= dayStr;
}

/** Check if event overlaps a date range [rangeStart, rangeEnd] (inclusive, YYYY-MM-DD strings) */
export function eventOverlapsRange(
  event: CalendarEvent,
  rangeStart: string,
  rangeEnd: string,
): boolean {
  return event.date <= rangeEnd && eventEndDate(event) >= rangeStart;
}

/** Filter events that overlap a specific day */
export function eventsForDay(events: CalendarEvent[], dayStr: string): CalendarEvent[] {
  return events.filter((e) => eventOverlapsDay(e, dayStr));
}

/** Filter events that overlap a date range */
export function eventsForRange(
  events: CalendarEvent[],
  rangeStart: string,
  rangeEnd: string,
): CalendarEvent[] {
  return events.filter((e) => eventOverlapsRange(e, rangeStart, rangeEnd));
}

/** Filter events that overlap a month (monthKey = "YYYY-MM") */
export function eventsForMonth(events: CalendarEvent[], monthKey: string): CalendarEvent[] {
  const rangeStart = `${monthKey}-01`;
  // End of month: parse then get last day
  const [y, m] = monthKey.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate(); // day 0 of next month = last day of this month
  const rangeEnd = `${monthKey}-${String(lastDay).padStart(2, '0')}`;
  return eventsForRange(events, rangeStart, rangeEnd);
}

/** Filter events that overlap a week (weekKey = "YYYY-Www") */
export function eventsForWeek(events: CalendarEvent[], weekKey: string): CalendarEvent[] {
  const weekStart = getWeekStart(weekKey);
  const weekEnd = getWeekEnd(weekKey);
  const startStr = weekStart.toISOString().slice(0, 10);
  const endStr = weekEnd.toISOString().slice(0, 10);
  return eventsForRange(events, startStr, endStr);
}

/** Calculate total days an event spans */
export function eventTotalDays(event: CalendarEvent): number {
  const end = eventEndDate(event);
  if (end === event.date) return 1;
  const startMs = parseCalendarDate(event.date).getTime();
  const endMs = parseCalendarDate(end).getTime();
  return Math.round((endMs - startMs) / MS_PER_DAY) + 1;
}

/** Check if an event is multi-day */
export function isMultiDayEvent(event: CalendarEvent): boolean {
  return eventTotalDays(event) > 1;
}

/**
 * For a multi-day event shown in a given period, compute which "page" of the event this is.
 * periodStart: YYYY-MM-DD string for the start of the display period (e.g. week start, month start)
 * Returns { dayIndex: 1-based position within event, totalDays }
 */
export function eventDayIndex(
  event: CalendarEvent,
  dayStr: string,
): { dayIndex: number; totalDays: number } {
  const totalDays = eventTotalDays(event);
  if (totalDays === 1) return { dayIndex: 1, totalDays: 1 };
  const startMs = parseCalendarDate(event.date).getTime();
  const dayMs = parseCalendarDate(dayStr).getTime();
  const dayIndex = Math.round((dayMs - startMs) / MS_PER_DAY) + 1;
  return { dayIndex: Math.max(1, Math.min(dayIndex, totalDays)), totalDays };
}

/**
 * For a multi-day event shown in a week context, compute week-level span info.
 * Returns { weekIndex: 1-based, totalWeeks }
 */
export function eventWeekSpan(
  event: CalendarEvent,
  weekKey: string,
): { weekIndex: number; totalWeeks: number } {
  const totalDays = eventTotalDays(event);
  if (totalDays <= 7) return { weekIndex: 1, totalWeeks: 1 };

  const totalWeeks = Math.ceil(totalDays / 7);
  const eventStartMs = parseCalendarDate(event.date).getTime();
  const weekStartDate = getWeekStart(weekKey);
  const weekStartMs = weekStartDate.getTime();
  const dayOffset = Math.max(0, Math.round((weekStartMs - eventStartMs) / MS_PER_DAY));
  const weekIndex = Math.floor(dayOffset / 7) + 1;
  return { weekIndex: Math.min(weekIndex, totalWeeks), totalWeeks };
}

/**
 * For a multi-day event shown in a month context, compute month-level span info.
 * Returns { monthIndex: 1-based, totalMonths }
 */
export function eventMonthSpan(
  event: CalendarEvent,
  monthKey: string,
): { monthIndex: number; totalMonths: number } {
  const end = eventEndDate(event);
  const [startY, startM] = event.date.split('-').map(Number);
  const [endY, endM] = end.split('-').map(Number);
  const totalMonths = (endY - startY) * 12 + (endM - startM) + 1;
  if (totalMonths <= 1) return { monthIndex: 1, totalMonths: 1 };

  const [curY, curM] = monthKey.split('-').map(Number);
  const monthIndex = (curY - startY) * 12 + (curM - startM) + 1;
  return { monthIndex: Math.max(1, Math.min(monthIndex, totalMonths)), totalMonths };
}

// Event type colors
export const eventTypeColors: Record<string, string> = {
  appointment: 'bg-blue-100 text-blue-800',
  travel: 'bg-purple-100 text-purple-800',
  event: 'bg-green-100 text-green-800',
  deadline: 'bg-red-100 text-red-800',
};

export const eventTypeIcons: Record<string, string> = {
  appointment: '📅',
  travel: '✈️',
  event: '🎉',
  deadline: '⏰',
};

// ==========================================
// TASK PROGRESS UTILITIES
// ==========================================

/**
 * Returns a CSS style string with --progress custom property for a project.
 * Used to show a subtle background fill indicating task completion.
 * Returns empty string if no tasks or 0 total.
 */
export function progressStyle(project: { taskCounts?: { total: number; done: number } }): string {
  if (!project.taskCounts || project.taskCounts.total === 0) return '';
  const pct = Math.round((project.taskCounts.done / project.taskCounts.total) * 100);
  if (pct === 0) return '';
  return `--progress: ${pct}%;`;
}

/**
 * Returns true when all tasks in a project are done.
 */
export function progressComplete(project: {
  taskCounts?: { total: number; done: number };
}): boolean {
  if (!project.taskCounts || project.taskCounts.total === 0) return false;
  return project.taskCounts.done === project.taskCounts.total;
}

// ==========================================
// URL BUILDING UTILITIES
// ==========================================

/**
 * Determines whether a range param should be included in a URL.
 * Returns the range value to include, or null if it should be omitted (is default).
 *
 * The "days" view uses week-style ranges regardless of the current scale,
 * so we must check against the week default when view is "days".
 */
export function getEffectiveRangeParam(
  range: string | null | undefined,
  view: string,
  scale: string,
  defaultRanges: Record<string, string | null>,
  viewSpecificDefaultRanges?: Partial<Record<string, Partial<Record<string, string>>>>,
): string | null {
  if (!range) return null;
  // "days" view always uses week-style ranges, even at month scale
  const effectiveScale = view === 'days' ? 'week' : scale;
  // Use view-specific default if available, otherwise fall back to scale default
  const defaultRange =
    viewSpecificDefaultRanges?.[view]?.[effectiveScale] ?? defaultRanges[effectiveScale];
  return range !== defaultRange ? range : null;
}

// ==========================================
// TIME FORMATTING UTILITIES
// ==========================================

/**
 * Format a time string (HH:MM) to 12-hour format with AM/PM
 * "09:00" → "9:00 AM", "14:30" → "2:30 PM", "14:00" → "2 PM"
 */
export function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return m === 0 ? `${hour} ${suffix}` : `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * Format a time string to compact format
 * "09:00" → "9a", "14:30" → "2:30p", "14:00" → "2p"
 */
export function formatTimeShort(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'p' : 'a';
  const hour = h % 12 || 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, '0')}${suffix}`;
}

/**
 * Format an event's time range for display
 * Returns null for all-day events
 * For same-day timed events: "9 AM – 10:30 AM"
 * For multi-day timed events: returns just the start time (use formatEventFullRange for full display)
 * For zero-duration: just "9 AM"
 */
export function formatEventTimeRange(event: CalendarEvent): string | null {
  if (!event.startTime) return null;

  const start = formatTime(event.startTime);

  // Zero-duration event
  if (!event.endTime) {
    return start;
  }

  // Multi-day timed event - times apply to different dates
  // Return just the time portion; caller should use formatEventFullRange for full display
  if (event.endDate && event.endDate !== event.date) {
    return `${start} – ${formatTime(event.endTime)}`;
  }

  // Same-day event
  return `${start} – ${formatTime(event.endTime)}`;
}

/**
 * Format a multi-day timed event's full date+time range
 * e.g., "Feb 11 at 9 AM → Feb 13 at 10 AM"
 * Returns null for all-day or same-day events
 */
export function formatEventFullRange(event: CalendarEvent): string | null {
  // Only for multi-day timed events
  if (!event.startTime || !event.endDate || event.endDate === event.date) {
    return null;
  }

  const startDate = parseCalendarDate(event.date);
  const endDate = parseCalendarDate(event.endDate);

  const startDateStr = formatCalendarDate(startDate, { month: 'short', day: 'numeric' });
  const endDateStr = formatCalendarDate(endDate, { month: 'short', day: 'numeric' });

  const startTimeStr = formatTime(event.startTime);

  if (event.endTime) {
    const endTimeStr = formatTime(event.endTime);
    return `${startDateStr} at ${startTimeStr} → ${endDateStr} at ${endTimeStr}`;
  }

  // Zero-duration multi-day? (unlikely but handle it)
  return `${startDateStr} at ${startTimeStr} → ${endDateStr}`;
}

/**
 * Format an event's time for compact display (e.g., in lists)
 * Returns null for all-day events
 * For timed events: "9a" or "2:30p"
 */
export function formatEventTimeShort(event: CalendarEvent): string | null {
  if (!event.startTime) return null;
  return formatTimeShort(event.startTime);
}

/**
 * Sort events for display:
 * 1. All-day single-day events first
 * 2. Then all other events sorted by start time (all-day multi-day, then timed)
 *
 * For events with same priority, maintains original order (stable sort)
 */
export function sortEventsForDisplay(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort((a, b) => {
    const aIsAllDaySingleDay = !a.startTime && !a.endDate;
    const bIsAllDaySingleDay = !b.startTime && !b.endDate;

    // All-day single-day events come first
    if (aIsAllDaySingleDay && !bIsAllDaySingleDay) return -1;
    if (!aIsAllDaySingleDay && bIsAllDaySingleDay) return 1;

    // Both all-day single-day: sort by date
    if (aIsAllDaySingleDay && bIsAllDaySingleDay) {
      return a.date.localeCompare(b.date);
    }

    // For timed events, sort by start time
    if (a.startTime && b.startTime) {
      return a.startTime.localeCompare(b.startTime);
    }

    // All-day multi-day events come before timed events (they span the whole day)
    if (!a.startTime && b.startTime) return -1;
    if (a.startTime && !b.startTime) return 1;

    // Both all-day multi-day: sort by start date
    return a.date.localeCompare(b.date);
  });
}

/**
 * Calculate duration between two HH:MM time strings in minutes
 */
export function timeDurationMinutes(startTime: string, endTime: string): number {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  return endH * 60 + endM - (startH * 60 + startM);
}

/**
 * Format a duration in minutes to human-readable string
 * e.g., 90 → "1h 30m", 60 → "1h", 30 → "30m"
 */
export function formatDuration(minutes: number): string {
  if (minutes < 0) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Get formatted duration for a timed event
 * Returns null for all-day events or if times are invalid
 */
export function formatEventDuration(event: CalendarEvent): string | null {
  if (!event.startTime || !event.endTime) return null;

  // For same-day events, calculate time difference
  if (!event.endDate || event.endDate === event.date) {
    const minutes = timeDurationMinutes(event.startTime, event.endTime);
    if (minutes <= 0) return null;
    return formatDuration(minutes);
  }

  // For multi-day timed events, show day count
  const days = eventTotalDays(event);
  return `${days} days`;
}
